import { useState, useCallback, useMemo } from 'react';
import { useEffect } from 'react';
import axios from '../../../utils/axiosConfig';
import { API_URL } from '../../../config';
import * as scriptService from '../../../services/scriptService';
import * as shootingScheduleService from '../../../services/shootingScheduleService';
import { TAG_TYPES } from '../../../components/SimpleScreenplayViewer';

/**
 * useNotesAutoFill
 * Scaffold hook that will later derive department note suggestions
 * from the shooting schedule and breakdown for the current shooting day.
 *
 * For now, returns empty suggestions and a no-op regenerate.
 */
export default function useNotesAutoFill({ projectId, shootingDay }) {
  const [loading, setLoading] = useState(false);
  const [lastGeneratedAt, setLastGeneratedAt] = useState(null);

  // Mutable suggestions state so we can update after fetching
  const [suggestions, setSuggestions] = useState(() => ({
    camera: [],
    lighting: [],
    props: [],
    sound: [],
    vfx: [],
    specialFx: [],
    vehicles: [],
    stunts: [],
    animals: [],
    hairMakeup: [],
    misc: []
  }));

  const baseSuggestions = useMemo(() => ({
    camera: [],
    lighting: [],
    props: [],
    sound: [],
    vfx: [],
    specialFx: [],
    vehicles: [],
    stunts: [],
    animals: [],
    hairMakeup: [],
    misc: []
  }), []);

  const unique = (arr) => Array.from(new Set(arr.filter(Boolean).map(s => s.trim())));

  // Helper: Build mapping of tags per scene using script content ranges
  const buildSceneBreakdownMap = useCallback((scenesList, tags, content) => {
    const byId = {};
    const lines = content ? content.split('\n') : [];

    const findSceneRange = (scene, index) => {
      if (scene.startLine !== undefined && scene.endLine !== undefined) {
        return { start: parseInt(scene.startLine), end: parseInt(scene.endLine) };
      }
      if (scene.range && (scene.range.start !== undefined)) {
        return { start: parseInt(scene.range.start) || 0, end: parseInt(scene.range.end) || Number.MAX_SAFE_INTEGER };
      }
      if (lines.length && (scene.heading || scene.text)) {
        const searchText = scene.heading || scene.text;
        let startIdx = -1;
        if (searchText) {
          startIdx = lines.findIndex(line => line && line.includes(searchText));
          if (startIdx === -1 && searchText.length > 10) {
            const partial = searchText.substring(0, 10);
            startIdx = lines.findIndex(line => line && line.includes(partial));
          }
        }
        if (startIdx !== -1) {
          const nextIdx = lines.findIndex((line, idx) => idx > startIdx && line && /^(INT\.|EXT\.|INT |EXT )/i.test(line.trim()));
          return { start: startIdx, end: nextIdx !== -1 ? nextIdx - 1 : lines.length - 1 };
        }
      }
      // fallback: approximate by index position
      const total = lines.length || 1000;
      const avg = total / Math.max(1, scenesList.length);
      return { start: Math.floor(index * avg), end: Math.floor((index + 1) * avg) - 1 };
    };

    (scenesList || []).forEach((scene, idx) => {
      const { start, end } = findSceneRange(scene, idx);
      const sceneId = scene.id || scene._id || `scene_${idx}`;
      const sceneNumber = (scene.number || '').toString();
      const sceneTags = (tags || []).filter(t => {
        const ln = parseInt(t.lineNumber);
        const inRange = !isNaN(ln) && ln >= start && ln <= end;
        const sceneIdMatch = t.sceneId && sceneId && t.sceneId === sceneId;
        const numberMatch = sceneNumber && (t.sceneNumber?.toString && t.sceneNumber.toString() === sceneNumber);
        return inRange || sceneIdMatch || numberMatch;
      });

      const grouped = sceneTags.reduce((acc, tag) => {
        let category = (tag.type || 'PROPS').toString().toUpperCase();
        if (category === 'CAST') category = 'CHARACTER';
        if (!TAG_TYPES[category]) category = 'PROPS';
        if (!acc[category]) acc[category] = [];
        acc[category].push(tag);
        return acc;
      }, {});

      const cameraNotes = [
        ...(grouped.CAMERA || []),
        ...(grouped.PRODUCTION_NOTES || []).filter(t => /camera|lens|frame|shot|coverage/i.test(t.text || ''))
      ].map(t => t.text);

      byId[sceneId] = { grouped, cameraNotes };
    });

    return byId;
  }, []);

  const regenerate = useCallback(async () => {
    if (!projectId) return;
    try {
      setLoading(true);

      // 0) Prefer schedule-published per-day department items if available (from ShootingScheduleTab)
      try {
        const rawPub = localStorage.getItem(`schedule_pub_${projectId}`);
        if (rawPub) {
          const pub = JSON.parse(rawPub);
          const dayKey = String(Math.max(1, parseInt(shootingDay) || 1));
          const dayAgg = pub?.days?.[dayKey];
          if (dayAgg && typeof dayAgg === 'object') {
            const toArray = (v) => Array.isArray(v) ? v : [];
            const mapped = {
              camera: toArray(dayAgg.camera),
              lighting: toArray(dayAgg.lighting),
              props: toArray(dayAgg.props),
              sound: toArray(dayAgg.sound),
              vfx: toArray(dayAgg.vfx),
              specialFx: toArray(dayAgg.specialFx),
              vehicles: toArray(dayAgg.vehicles),
              stunts: toArray(dayAgg.stunts),
              animals: toArray(dayAgg.animals),
              hairMakeup: toArray(dayAgg.hairMakeup),
              misc: toArray(dayAgg.misc),
            };
            // Deduplicate and cap
            const deduped = Object.keys(mapped).reduce((m, key) => {
              m[key] = Array.from(new Set((mapped[key] || []).map(s => (s || '').trim()).filter(Boolean))).slice(0, 25);
              return m;
            }, {});
            if (process.env.NODE_ENV === 'development') {
              // eslint-disable-next-line no-console
              console.debug('useNotesAutoFill: using schedule-published data for day', dayKey, deduped);
            }
            setSuggestions(deduped);
            setLastGeneratedAt(Date.now());
            setLoading(false);
            return; // Use published data and skip fallback pipeline
          }
        }
      } catch {}

      // Strict behavior: If no schedule-published data exists, emit empty suggestions and return.
      const empty = { camera: [], lighting: [], props: [], sound: [], vfx: [], specialFx: [], vehicles: [], stunts: [], animals: [], hairMakeup: [], misc: [] };
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.debug('useNotesAutoFill: no schedule-published data; emitting empty suggestions');
      }
      setSuggestions(empty);
      setLastGeneratedAt(Date.now());
      setLoading(false);
      return;
    } catch (e) {
      // ignore
    }
  }, [projectId, shootingDay]);

  // Auto-generate on first render and when dependencies change
  useEffect(() => {
    // Debounce slightly to avoid double calls on rapid state changes
    const t = setTimeout(() => {
      regenerate();
    }, 50);
    return () => clearTimeout(t);
  }, [regenerate]);

  // React to schedule publisher updates and storage changes
  useEffect(() => {
    const onPub = (e) => {
      try {
        if (!e || !e.detail) return regenerate();
        if (e.detail.projectId === projectId) regenerate();
      } catch {}
    };
    const onStorage = (e) => {
      try {
        if (!e || !e.key) return;
        if (e.key === `schedule_pub_${projectId}`) regenerate();
      } catch {}
    };
    window.addEventListener('schedule_pub_updated', onPub);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('schedule_pub_updated', onPub);
      window.removeEventListener('storage', onStorage);
    };
  }, [projectId, regenerate]);

  return { suggestions, regenerate, loading, lastGeneratedAt };
}
